import { API_URL } from "../../utils/constants";
import { fetchWithAuth } from "../../utils/auth";
import { IAddress } from "../addressService";
import { IBankDetail } from "../bankDetailService";
import { DocumentType, IDocument } from "../documentService";

export interface IStatus {
  status_id: number;
  status_name: string;
}

export interface ISalesAgent {
  employee_id: number;
  first_name: string;
  last_name: string;
  avatar: string | null;
}

export interface IInvestor {
  investor_id: number;
  first_name: string;
  last_name: string;
  email: string;
  status: IStatus;
  sales_agent: ISalesAgent | null;
  avatar?: string | null;
  phone_number?: string | null;
  birth_date?: string;
  onboarding_step: number;
  handover_step: number;
  nationality?: string | null;
  language: string;
  created_at: string;
  updated_at: string;
  address: IAddress;
  documents: IDocument[];
  bank_details: IBankDetail[];
  has_golden_visa?: boolean;
  /** Set the first time the support tour was dismissed, on any device. */
  support_tour_seen_at?: string | null;
  owned_units?: { unit_owner_id: number; purchase_price: number }[];
}

export const fetchInvestor = async (): Promise<IInvestor> => {
  const response = await fetchWithAuth(`${API_URL}/investors`, {
    method: "GET",
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message);

  return data;
};

export const updateInvestor = async (
  email: string,
  phone_number: string,
  birth_date: string,
  nationality: string
): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/investors`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      phone_number,
      birth_date,
      nationality,
    }),
  });

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateOnboardingStep = async (
  investor_id: number,
  onboarding_step: number
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/${investor_id}/onboarding`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        onboarding_step,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateOnboardingPassword = async (
  investor_id: number,
  new_password: string
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/${investor_id}/onboarding-password`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        new_password,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateHandoverStep = async (
  investor_id: number,
  handover_step: number
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/${investor_id}/handover`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        handover_step,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateInvestorPassword = async (
  investor_id: number,
  current_password: string,
  new_password: string
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/${investor_id}/password`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        current_password,
        new_password,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const markSupportTourSeenOnAccount = async (): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/investors/support-tour-seen`, { method: "POST" });
  if (!response.ok) throw new Error(`Could not save the support tour as seen (${response.status})`);
};

export const updateInvestorLanguage = async (
  language: string
): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/investors/language`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ language }),
  });

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const uploadInvestorAvatar = async (
  avatarFile: File
): Promise<string> => {
  const formData = new FormData();
  formData.append("avatar", avatarFile);

  const response = await fetchWithAuth(`${API_URL}/investors/avatar`, {
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();
  if (response.status === 200) return data.avatar;
  else throw new Error(data.message);
};

export const uploadInvestorDocuments = async (
  document_type: DocumentType,
  files: File[],
  valid_until_dates: (string | null)[] = [],
  document_identifiers: (string | null)[] = []
): Promise<IDocument[]> => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`file_${index}`, file);
    if (valid_until_dates[index]) {
      formData.append(
        `valid_until_${index}`,
        valid_until_dates[index] as string
      );
    }
    if (document_identifiers[index]) {
      formData.append(
        `document_identifier_${index}`,
        document_identifiers[index] as string
      );
    }
  });

  const response = await fetchWithAuth(
    `${API_URL}/investors/${document_type}`,
    {
      method: "POST",
      body: formData,
    }
  );

  const data = await response.json();
  if (response.status === 201) return data.documents;
  else throw new Error(data.message);
};

export const updatePassportIdentifier = async (
  document_identifier: string | null
): Promise<IDocument> => {
  const response = await fetchWithAuth(`${API_URL}/investors/passport`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      document_identifier,
    }),
  });

  const data = await response.json();
  if (response.status === 200) return data;
  else throw new Error(data);
};

export const downloadInvestorDocument = async (
  investor_id: number,
  document_id: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/${investor_id}/documents/${document_id}/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const fetchCompletionCertificates = async (): Promise<any[]> => {
  const response = await fetchWithAuth(
    `${API_URL}/investors/completion-certificates`,
    {
      method: "GET",
    }
  );

  const data = await response.json();
  if (!response.ok) throw new Error(data.message);

  return data.documents;
};
